import {
    createAsyncThunk,
    createSlice
} from '@reduxjs/toolkit';
import apiService from '../../Services/apiServices';
const initialState = {
    partners: [],
    outlets: [],
    outletBillingSubscriptions: {},
    subscriptionPlans: [],
    alertQueue: [],
    automatedAlertQueue: [],
    planChangeRequests: [],
    automatedAlertFetchSummary: null,
    settings: null,
    emailVerificationRequired: true,
    status: 'idle',
    actionStatus: 'idle',
    error: null
};
const formatApiDetail = (detail, fallback) => {
    if (!detail) {
        return fallback;
    }
    if (typeof detail === 'string') {
        return detail;
    }
    if (Array.isArray(detail)) {
        const messages = detail.map((item) => {
            if (typeof item === 'string') {
                return item;
            }
            if (!item || typeof item !== 'object') {
                return '';
            }
            const field = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body').join('.') :
                '';
            if (field === 'duration_months') {
                return 'Set the duration for at least one month.';
            }
            return [field, item.msg].filter(Boolean).join(': ');
        }).filter(Boolean);
        return messages.length > 0 ? messages.join(' ') : fallback;
    }
    if (typeof detail === 'object') {
        return detail.message || detail.msg || JSON.stringify(detail);
    }
    return String(detail);
};
const extractDetail = (error, fallback) => formatApiDetail(error?.response?.data?.detail, fallback);
export const loadPartners = createAsyncThunk('superAdminWorkspace/loadPartners', async (filters = {}, thunkAPI) => {
    try {
        const response = await apiService.get('/barbellOSAdmin/partners/get', filters);
        return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load partners'));
    }
});
export const loadOutlets = createAsyncThunk('superAdminWorkspace/loadOutlets', async (filters = {}, thunkAPI) => {
    try {
        const response = await apiService.get('/barbellOSAdmin/outlets/get', filters);
        return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load outlets'));
    }
});
export const loadPartnerSettings = createAsyncThunk('superAdminWorkspace/loadPartnerSettings', async (_, thunkAPI) => {
    try {
        const response = await apiService.get('/barbellOSAdmin/settings');
        return response?.data?.data || {
            email_verification_required: true
        };
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load settings'));
    }
});
export const updateSuperAdminSettings = createAsyncThunk('superAdminWorkspace/updateSuperAdminSettings', async (payload, thunkAPI) => {
    try {
        const response = await apiService.put('/barbellOSAdmin/settings', payload);
        await thunkAPI.dispatch(loadSubscriptionPlans());
        await thunkAPI.dispatch(loadAlertQueue());
        await thunkAPI.dispatch(loadAutomatedAlertQueue());
        return response?.data?.data || payload;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to update settings'));
    }
});
export const loadSubscriptionPlans = createAsyncThunk('superAdminWorkspace/loadSubscriptionPlans', async (_,
    thunkAPI) => {
        try {
            const response = await apiService.get('/barbellOSAdmin/subscriptions/plans/all');
            return Array.isArray(response?.data?.data) ? response.data.data : [];
        } catch (error) {
            return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load subscription plans'));
        }
    });
export const setOutletBillingSubscription = createAsyncThunk('superAdminWorkspace/setOutletBillingSubscription',
async ({
    outletId,
    tier
}, thunkAPI) => {
    try {
        const response = await apiService.post('/barbellOSBilling/admin/outlet/subscription', {
            outlet_id: outletId,
            tier
        });
        return response?.data?.data || null;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to set outlet billing subscription'));
    }
});
export const loadOutletBillingSubscription = createAsyncThunk('superAdminWorkspace/loadOutletBillingSubscription',
async (outletId, thunkAPI) => {
        try {
            const response = await apiService.get(`/barbellOSBilling/admin/outlet/${outletId}/subscription`);
            return {
                outletId,
                data: response?.data?.data || null
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load outlet billing subscription'));
        }
    });
export const loadAlertQueue = createAsyncThunk('superAdminWorkspace/loadAlertQueue', async (_, thunkAPI) => {
    try {
        const response = await apiService.get('/barbellOSBilling/alerts', {
            source: 'manual'
        });
        return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load alert queue'));
    }
});
export const loadAutomatedAlertQueue = createAsyncThunk('superAdminWorkspace/loadAutomatedAlertQueue', async (_,
    thunkAPI) => {
    try {
        const response = await apiService.get('/barbellOSBilling/alerts', {
            source: 'automated'
        });
        return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load automated alert queue'));
    }
});
export const loadPlanChangeRequests = createAsyncThunk('superAdminWorkspace/loadPlanChangeRequests', async (_, thunkAPI) => {
    try {
        const response = await apiService.get('/barbellOSBilling/admin/plan-change-requests');
        return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to load plan change requests'));
    }
});
export const fetchAutomatedAlerts = createAsyncThunk('superAdminWorkspace/fetchAutomatedAlerts', async (options = {},
thunkAPI) => {
    try {
        const response = await apiService.post('/barbellOSBilling/admin/alerts/automated/fetch', {}, { params: options });
        const data = response?.data?.data || {};
        await thunkAPI.dispatch(loadAutomatedAlertQueue());
        return data;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to fetch automated alerts'));
    }
});
export const sendQueuedAlerts = createAsyncThunk('superAdminWorkspace/sendQueuedAlerts', async (alertIds, thunkAPI) => {
    try {
        const response = await apiService.post('/barbellOSBilling/admin/alerts/send', { alert_ids: alertIds });
        await thunkAPI.dispatch(loadAutomatedAlertQueue());
        return response?.data?.data || {};
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to send selected emails'));
    }
});
export const updateAlertQueueStatus = createAsyncThunk('superAdminWorkspace/updateAlertQueueStatus', async ({
    alertId,
    status,
    failureReason = '',
    source = 'manual'
}, thunkAPI) => {
    try {
        const response = await apiService.patch(`/barbellOSBilling/admin/alerts/${alertId}/status`, {
            status,
            failure_reason: failureReason || null
        });
        if (source === 'automated') {
            await thunkAPI.dispatch(loadAutomatedAlertQueue());
        } else {
            await thunkAPI.dispatch(loadAlertQueue());
        }
        return response?.data?.data || null;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to update alert status'));
    }
});
export const createPartnerStepOne = createAsyncThunk('superAdminWorkspace/createPartnerStepOne', async (payload,
    thunkAPI) => {
    try {
        const response = await apiService.post('/barbellOSAdmin/onboard/a-step', payload);
        return response?.data?.data || null;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to create partner'));
    }
});
export const completePartnerOnboarding = createAsyncThunk('superAdminWorkspace/completePartnerOnboarding', async ({
    userId,
    files = [],
    docType = 'kyc'
}, thunkAPI) => {
    try {
        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('doc_type', docType);
        files.forEach((file) => formData.append('documents', file));
        await apiService.post('/barbellOSAdmin/onboard/complete', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        await thunkAPI.dispatch(loadPartners());
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to complete partner onboarding'));
    }
});
export const cancelPartnerOnboarding = createAsyncThunk('superAdminWorkspace/cancelPartnerOnboarding', async ({ userId }, thunkAPI) => {
    try {
        await apiService.post('/barbellOSAdmin/onboard/cancel', { user_id: userId, medium: 'email' });
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to cancel partner onboarding'));
    }
});
export const verifyPartnerOtp = createAsyncThunk('superAdminWorkspace/verifyPartnerOtp', async (payload, thunkAPI) => {
    try {
        const response = await apiService.post('/barbellOSAdmin/onboard/b-step/verifyOTP', payload);
        return response?.data?.data || null;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to verify OTP'));
    }
});
export const resendPartnerOtp = createAsyncThunk('superAdminWorkspace/resendPartnerOtp', async (payload, thunkAPI) => {
    try {
        await apiService.post('/barbellOSAdmin/resendOTP', payload);
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to resend OTP'));
    }
});
export const uploadPartnerDocuments = createAsyncThunk('superAdminWorkspace/uploadPartnerDocuments', async ({
    files,
    userId,
    docType
}, thunkAPI) => {
    try {
        const formData = new FormData();
        files.forEach((file) => formData.append('documents', file));
        formData.append('userId', userId);
        formData.append('doc_type', docType);
        await apiService.post('/barbellOSAdmin/upload/partner-documents', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        await thunkAPI.dispatch(loadPartners());
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to upload documents'));
    }
});
export const editPartner = createAsyncThunk('superAdminWorkspace/editPartner', async ({
    partnerId,
    details
}, thunkAPI) => {
    try {
        await apiService.put('/barbellOSAdmin/partners/edit-details', details, {
            params: {
                partner_id: partnerId
            }
        });
        await thunkAPI.dispatch(loadPartners());
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to edit partner'));
    }
});
export const togglePartner = createAsyncThunk('superAdminWorkspace/togglePartner', async ({
    partnerId,
    activate
}, thunkAPI) => {
    try {
        const route = activate ? '/barbellOSAdmin/partners/activate' :
            '/barbellOSAdmin/partners/de-activate';
        await apiService.put(route, null, {
            params: {
                partner_id: partnerId
            }
        });
        await thunkAPI.dispatch(loadPartners());
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to update partner status'));
    }
});
export const createOutletStepOneForPartner = createAsyncThunk('superAdminWorkspace/createOutletStepOneForPartner',
async (payload, thunkAPI) => {
        try {
            const response = await apiService.post('/barbellOSAdmin/onboard/outlet', payload);
            await thunkAPI.dispatch(loadPartners());
            await thunkAPI.dispatch(loadOutlets());
            return response?.data?.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to add outlet'));
        }
    });
export const uploadOutletDocumentsForPartner = createAsyncThunk('superAdminWorkspace/uploadOutletDocumentsForPartner',
    async ({
        files,
        outletId,
        docType
    }, thunkAPI) => {
        try {
            const formData = new FormData();
            files.forEach((file) => formData.append('documents', file));
            formData.append('outletId', outletId);
            formData.append('doc_type', docType);
            await apiService.post('/barbellOSAdmin/upload/outlet-documents', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            await thunkAPI.dispatch(loadPartners());
            await thunkAPI.dispatch(loadOutlets());
            return true;
        } catch (error) {
            return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to upload outlet documents'));
        }
    });
export const uploadOutletLogo = createAsyncThunk('superAdminWorkspace/uploadOutletLogo', async ({
    outletId,
    file
}, thunkAPI) => {
    try {
        const formData = new FormData();
        formData.append('logo', file);
        formData.append('outletId', outletId);
        const response = await apiService.post('/barbellOSAdmin/outlets/upload-logo', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        await thunkAPI.dispatch(loadOutlets());
        return response?.data?.data || null;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to upload outlet logo'));
    }
});
export const editOutlet = createAsyncThunk('superAdminWorkspace/editOutlet', async ({
    outletId,
    details
}, thunkAPI) => {
    try {
        await apiService.put('/barbellOSAdmin/outlets/edit-details', details, {
            params: {
                outlet_id: outletId
            }
        });
        await thunkAPI.dispatch(loadOutlets());
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to edit outlet'));
    }
});
export const toggleOutlet = createAsyncThunk('superAdminWorkspace/toggleOutlet', async ({
    outletId,
    activate
}, thunkAPI) => {
    try {
        const route = activate ? '/barbellOSAdmin/outlets/activate' : '/barbellOSAdmin/outlets/de-activate';
        await apiService.put(route, null, {
            params: {
                outlet_id: outletId
            }
        });
        await thunkAPI.dispatch(loadOutlets());
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to update outlet status'));
    }
});
export const deleteOutlet = createAsyncThunk('superAdminWorkspace/deleteOutlet', async ({
    outletId
}, thunkAPI) => {
    try {
        await apiService.delete('/barbellOSAdmin/outlets/delete', {
            params: {
                outlet_id: outletId
            }
        });
        await thunkAPI.dispatch(loadOutlets());
        return true;
    } catch (error) {
        return thunkAPI.rejectWithValue(extractDetail(error, 'Failed to delete outlet'));
    }
});
const superAdminWorkspaceSlice = createSlice({
    name: 'superAdminWorkspace',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(loadPartners.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        }).addCase(loadPartners.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.partners = action.payload;
        }).addCase(loadPartners.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Unable to load partners';
        }).addCase(loadOutlets.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        }).addCase(loadOutlets.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.outlets = action.payload;
        }).addCase(loadOutlets.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Unable to load outlets';
        }).addCase(loadPartnerSettings.fulfilled, (state, action) => {
            state.settings = action.payload || null;
            state.emailVerificationRequired = action.payload?.email_verification_required !== false;
        }).addCase(updateSuperAdminSettings.fulfilled, (state, action) => {
            state.settings = action.payload || null;
            state.emailVerificationRequired = action.payload?.email_verification_required !== false;
        }).addCase(loadSubscriptionPlans.fulfilled, (state, action) => {
            state.subscriptionPlans = action.payload || [];
        }).addCase(loadAlertQueue.fulfilled, (state, action) => {
            state.alertQueue = action.payload || [];
        }).addCase(loadAutomatedAlertQueue.fulfilled, (state, action) => {
            state.automatedAlertQueue = action.payload || [];
        }).addCase(loadPlanChangeRequests.fulfilled, (state, action) => {
            state.planChangeRequests = action.payload || [];
        }).addCase(fetchAutomatedAlerts.fulfilled, (state, action) => {
            state.automatedAlertFetchSummary = action.payload || null;
            if (Array.isArray(action.payload?.alerts)) {
                state.automatedAlertQueue = action.payload.alerts;
            }
        }).addCase(loadOutletBillingSubscription.fulfilled, (state, action) => {
            state.outletBillingSubscriptions[action.payload.outletId] = action.payload.data;
        }).addCase(setOutletBillingSubscription.fulfilled, (state, action) => {
            if (action.payload?.outlet_id) {
                state.outletBillingSubscriptions[action.payload.outlet_id] = action.payload;
            }
        }).addMatcher((action) => action.type.startsWith('superAdminWorkspace/') && action.type
            .endsWith('/pending') && !action.type.includes('loadPartners'), (state) => {
                state.actionStatus = 'loading';
                state.error = null;
            }).addMatcher((action) => action.type.startsWith('superAdminWorkspace/') && action.type
            .endsWith('/fulfilled') && !action.type.includes('loadPartners'), (state) => {
                state.actionStatus = 'succeeded';
            }).addMatcher((action) => action.type.startsWith('superAdminWorkspace/') && action.type
            .endsWith('/rejected') && !action.type.includes('loadPartners'), (state, action) => {
                state.actionStatus = 'failed';
                state.error = action.payload || 'Operation failed';
            });
    }
});
export default superAdminWorkspaceSlice.reducer;
